/**
 * Resolves which theme pack should be active.
 *
 * Precedence:
 * 1. Per-user theme when user_settings_select_theme is enabled and the user has a valid theme.
 * 2. Tenant `app_theme` when set and valid.
 * 3. Tenant `default_theme` (Hub still ships this key; `app_theme` falls back to it).
 * 4. Client-code bundled overlay default when both Hub keys are unset/empty.
 * 5. null — keep the build-time default (first Vuetify paint) unchanged.
 *
 * The first non-empty candidate that is not a known pack is rejected (`unknownAppTheme`)
 * and does not fall through to a later candidate.
 */
function normalizeThemeValue(value: unknown): string | null {
  if (value == null || value === '') {
    return null;
  }

  return String(value);
}

function isKnownThemePack(knownThemePacks: Record<string, unknown>, name: string): boolean {
  return Object.hasOwn(knownThemePacks, name);
}

export function resolveActiveThemePackName({
  appTheme,
  defaultTheme,
  clientCodeDefaultTheme,
  userTheme,
  userThemeSelectionEnabled,
  knownThemePacks = {},
}: {
  appTheme?: unknown;
  defaultTheme?: unknown;
  clientCodeDefaultTheme?: unknown;
  userTheme?: unknown;
  userThemeSelectionEnabled?: boolean;
  knownThemePacks?: Record<string, unknown>;
}): { theme: string | null; unknownAppTheme?: string } {
  if (userThemeSelectionEnabled && typeof userTheme === 'string' && userTheme && isKnownThemePack(knownThemePacks, userTheme)) {
    return { theme: userTheme };
  }

  const candidates = [appTheme, defaultTheme, clientCodeDefaultTheme];

  for (const candidate of candidates) {
    const name = normalizeThemeValue(candidate);

    if (!name) {
      continue;
    }

    if (isKnownThemePack(knownThemePacks, name)) {
      return { theme: name };
    }

    return { theme: null, unknownAppTheme: name };
  }

  return { theme: null };
}
